<template>
    <page :busy="busy" :tabs="tabs" :compact-mobile-view="true">
        <grid
            ref="grid"
            :defaultColDef="defaultColDef"
            :columnDefs="columnDefs"
            :treeData="false"
            :getDataPath="getDataPath"
            :isExternalFilterPresent="isExternalFilterPresent"
            :doesExternalFilterPass="doesExternalFilterPass"
            :rowData="partners"
        >
            <responsive-button
                :icon="['fas', 'plus']"
                modal="create-partner-modal"
                variant="primary"
                >
                Create partner card
            </responsive-button>
            <CreatePartnerModal
                :options="options"
                @startCreating="setBusyTrue"
                @endCreating="setBusyFalse"
            />
        </grid>
    </page>
</template>


<script>
import { mapGetters } from 'vuex';
import API from '../../../../api'
import Page from '../../../generic/page/page.vue'
import ActionsCellRenderer from './actions-cell-renderer.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import Grid from '../../../generic/grid/grid.vue';
import CreatePartnerModal from './create-partner-modal.vue';

export default {
    name: 'PartnerAdminData',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        CreatePartnerModal,
        actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            tabs: [
                {
                    title: 'Partner card categories',
                    route: '/app/partner-cards/admin/',
                    active: false,
                },
                {
                    title: 'Partner card data',
                    route: '/app/partner-cards/admin/card-data/',
                    active: true,
                },
                {
                    title: 'Partner card tooltips',
                    route: '/app/partner-cards/admin/card-tooltips/',
                    active: false,
                },
            ],
            busy: false,
            partners: [],
            defaultColDef: {
                autoHeight: true,
                sortable: true
            },
            getDataPath: data => data.id,
            options: [{value: null, text:'Select category for partner'}],
        };
    },

    computed: {
        ...mapGetters('partner', ['categoryIds', 'categoryNameById']),
        columnDefs() {
            return [
                {
                    field: 'id',
                    headerName: 'ID',
                    editable: false,
                    maxWidth: 90,
                },
                {
                    type: 'editableColumn',
                    field: 'title',
                    headerName: 'Card Title',
                    flex: 1,
                    minWidth: 150,
                    editable: ({ data }) => !data.deleted,
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/info/${data.id}/`,
                                    {
                                        title: newValue,
                                    }
                                );
                                const message = '#' + data.id + ' Title has been updated to ' + newValue;
                                this.successToast({text: message});
                            } catch (error) {
                                this.errorToast({text: 'Could not save card title.'});
                                data.title = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.title = newValue;
                        return true;
                    },
                },
                {
                    type: 'editableColumn',
                    field: 'subtitle',
                    headerName: 'Card Subtitle',
                    flex: 1,
                    minWidth: 110,
                    editable: ({ data }) => !data.deleted,
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/info/${data.id}/`,
                                    {
                                        subtitle: newValue,
                                    }
                                );
                                const message = '#' + data.id + ' Subitle has been updated to ' + newValue;
                                this.successToast({text: message});
                            } catch (error) {
                                this.errorToast({text: 'Could not save card subtitle.'})
                                data.subtitle = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.subtitle = newValue;
                        return true;
                    },
                },
                {
                    field: 'homepage',
                    headerName: 'Website Link',
                    editable: ({ data }) => true,
                    cellRenderer: ({ data }) =>
                        `<a href="${data.homepage}" target="_blank">${data.homepage}</a>`,
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/info/${data.id}/`,
                                    {
                                        homepage: newValue,
                                    }
                                );
                                const message = '#' + data.id + ' homepage has been updated to ' + newValue;
                                this.successToast({text: message});
                            } catch (error) {
                                this.errorToast({text: 'Could not save card homepage.'})
                                data.homepage = `<a href="${newValue}" target="_blank">${newValue}</a>`;
                                node.setData(data);
                            }
                        })();
                        data.homepage = newValue;
                        return true;
                    },
                },
                {
                    field: 'logo',
                    headerName: 'Icon',
                    editable: false,
                    cellRenderer: ({ data }) => data.logo ?
                        `<img src="${data.logo}" width="35" height="35">`
                        : ''
                },
                {
                    headerName: 'Tooltips count',
                    valueGetter: ({ data }) => data.tooltips.length,
                },
                {
                    field: 'category.id',
                    headerName: 'Category',
                    sort: 'asc',
                    flex: 1,
                    minWidth: 120,
                    cellRenderer: ({ data, value }) => this.categoryNameById(value),
                    editable: ({ data }) => !data.disabled,
                    cellClass: ({ data }) =>
                        data.disabled
                            ? 'text-muted'
                            : '',
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorParams: () => ({
                        values: this.categoryIds,
                        cellRenderer: ({ value }) =>
                            this.categoryNameById(value),
                    }),
                    valueSetter: ({ oldValue, newValue, node, data }) => {
                        (async () => {
                            try {
                                await API.put(
                                    `/app/partner-cards/info/${data.id}/`,
                                    {
                                        category: newValue,
                                    }
                                );
                                this.successToast({text: 'Partner category successfully updated.'})
                            } catch (error) {
                                this.errorToast({text: 'Could not save category.'})
                                data.category.id = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.category.id = newValue;

                        return true;

                    },
                },
                {
                    type: 'switchColumn',
                    field: 'deleted',
                    headerName: 'Enable',
                    valueGetter: ({ data }) => !data.deleted,
                    cellRendererParams: {
                        msgOn: "Are you sure you want to enable this partner?",
                        msgOff: "Are you sure you want to disable this partner?",
                        onChange: async ({ node, api }, newValue) => {
                            const row = node.data;
                            const disabled = !newValue;
                            this.busy = true;
                            try {
                                await API.put(
                                    `/app/partner-cards/info/${row.id}/`,
                                    {
                                        deleted: disabled,
                                    }
                                );
                                var message =
                                    'Partner successfully ' +
                                    (disabled ? 'disabled.' : 'enabled.');
                                this.successToast({text: message})
                            } catch (error) {
                                this.busy = false;
                                this.errorToast({text: 'Could not disable partner'})
                            }
                            const updateRows = [row];
                            const updateNodes = [node];
                            row.deleted = disabled;
                            api.applyTransaction({ update: updateRows });
                            api.redrawRows({ rowNodes: updateNodes });
                            this.busy = false;
                        },
                    },
                },
                {
                    headerName: '',
                    minWidth: 190,
                    resizable: false,
                    cellStyle: {textAlign: "center"},
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        update: () => {
                           this.fetchPartners();
                        },
                    },
                },

            ]
        },

        isExternalFilterPresent() {
            return () => false;
        },
        doesExternalFilterPass() {
            return node => !node.data.deleted;
        },
    },

    beforeCreate() {
        document.body.className = 'lx-partner-card-data-admin';
    },

    mounted() {
        (async () => {
                this.busy = true;
                try {
                    await this.$store.dispatch('partner/fetchPartnerCategories');
                    for (let i = 0; i < this.categoryIds.length; i++) {
                        this.options.push({
                            value: this.categoryIds[i],
                            text: this.categoryNameById(this.categoryIds[i])
                        })
                    }
                } catch (err) {
                    this.errorToast({text: 'Could not load category data.'})
                }
                await this.fetchPartners();
                this.busy = false;
            })();
    },

    methods: {
        async fetchPartners() {
            try {
                const response = await API.get('/app/partner-cards/info/');
                this.partners = response.data;
            } catch (err) {
                this.errorToast({text: 'Could not load partner cards data.'})
            }
        },

        setBusyTrue() {
            this.busy = true;
        },
        async setBusyFalse() {
            await this.fetchPartners();
            this.busy = false;
        }
    }
}
</script>


<style lang="scss">
    body.lx-partner-card-data-admin {
        overflow: hidden;
    }
</style>
